/**
 * CopyArticleService – builds a "copy cells to non-main market" request.
 * React port of Angular CopyArticleService.
 *
 * When the user right-clicks on a main-market cell and chooses
 * "Copy cells to non-main market", this service collects the selected
 * (or right-clicked) cell data and triggers the parent callback.
 */
import type { GridApi } from 'ag-grid-community';
import type { Market } from '../models';
import type { Department } from '../models/organization';
import * as GridDataService from './gridDataService';
import { KPI_DISPLAY_NAME_TO_CODE, PLANLEVEL_GROUND, TIME_QUARTER } from '../constants';

export interface KpiDataItem {
  id: string;
  kpiCode: string;
  weekWithYear: string;
  value: number;
  isLatestSeason: boolean;
}

export interface CopyArticleContext {
  gridApi: GridApi;
  selectedCells: Set<string>;
  selectedCellsCount: number;
  currentContextMenuCell: { node: { data: Record<string, unknown> }; column: { getColId(): string }; value: unknown } | null;
  selectedRegions: Market[];
  isStoreWorkspaceTab: boolean;
  selectedDepartmentInfo: Department | null | undefined;
  readonlyPlan: boolean | undefined;
}

interface CellData {
  node: { data: Record<string, unknown> };
  column: { getColId(): string };
  value: unknown;
  fieldName: string;
  kpi: string;
}

/**
 * Build KPI data items for the "copy to all regions" action.
 * @returns KPI data items array, or null if validation fails.
 */
export function buildCopyToAllRegionsData(context: CopyArticleContext): KpiDataItem[] | null {
  const cellsToProcess = getCellsToProcess(context);
  if (cellsToProcess.length === 0) return null;

  const mainMarket = context.selectedRegions.find((r) => r.isMainMarket);
  if (!mainMarket || !context.selectedDepartmentInfo?.departmentId) return null;

  const allKpiData = processKpiDataFromCells(cellsToProcess, mainMarket);
  return allKpiData.length > 0 ? allKpiData : null;
}

// ── Private helpers ────────────────────────────────────────────────

function getCellsToProcess(context: CopyArticleContext): CellData[] {
  if (context.selectedCellsCount > 0) {
    return Array.from(context.selectedCells)
      .map((key) => createCellDataFromKey(key, context.gridApi))
      .filter((cell): cell is CellData => cell !== null && isArticleDataCell(cell, context));
  }
  return getContextMenuCells(context);
}

function createCellDataFromKey(cellKey: string, gridApi: GridApi): CellData | null {
  const [rowIndexStr, ...fieldParts] = cellKey.split('_');
  const fieldName = fieldParts.join('_');
  const rowNode = gridApi.getDisplayedRowAtIndex(Number.parseInt(rowIndexStr));
  const column = gridApi.getColumn(fieldName);
  if (!rowNode || !column) return null;
  return {
    node: rowNode,
    column,
    fieldName,
    value: rowNode.data[fieldName],
    kpi: rowNode.data.kpi as string,
  };
}

function getContextMenuCells(context: CopyArticleContext): CellData[] {
  const cell = context.currentContextMenuCell;
  if (!cell?.node?.data || !cell?.column) return [];
  const cellData: CellData = {
    node: cell.node,
    column: cell.column,
    value: cell.value,
    fieldName: cell.column.getColId(),
    kpi: cell.node.data.kpi as string,
  };
  return isArticleDataCell(cellData, context) ? [cellData] : [];
}

function isArticleDataCell(cellData: CellData, context: CopyArticleContext): boolean {
  const colId = cellData.column?.getColId();
  if (!colId || colId === 'kpi' || colId === 'marketShortName') return false;

  const kpi = cellData.node?.data?.kpi as string | undefined;
  if (kpi?.includes('Sum') || kpi?.includes(TIME_QUARTER) || kpi?.includes('Fin Q')) return false;

  const cellFieldNameToWeekMap = GridDataService.getCellFieldNameToWeekMap();
  if (!cellFieldNameToWeekMap.has(colId) || cellData.node?.data?.isCalculated) return false;

  // Ground rows should not be copyable
  if (cellData.node?.data?.planLevel === PLANLEVEL_GROUND) return false;

  if (context.readonlyPlan) return false;

  const week = cellFieldNameToWeekMap.get(colId);
  const timeline = GridDataService.getTimelineMap();
  const currentWeek = timeline?.currentWeek ?? '';
  if (!week || Number(week) < Number(currentWeek)) return false;

  return true;
}

function processKpiDataFromCells(cellsToProcess: CellData[], mainMarket: Market): KpiDataItem[] {
  const weekToSeasonMap = GridDataService.getWeekToSeasonMap();
  const cellFieldNameToWeekMap = GridDataService.getCellFieldNameToWeekMap();

  return cellsToProcess.flatMap((cellData) => {
    const weekWithYear = cellFieldNameToWeekMap.get(cellData.fieldName);
    const seasonNumbers = weekWithYear ? weekToSeasonMap.get(weekWithYear) : null;
    const kpiCode = KPI_DISPLAY_NAME_TO_CODE[cellData.kpi];

    if (!weekWithYear || !seasonNumbers?.length || !kpiCode) return [];

    const marketCode = mainMarket.marketCode.toString();
    const currentValue = (cellData.value as number) || 0;
    const endingValue = (cellData.node.data[`${cellData.fieldName}_es`] as number) || 0;

    if (seasonNumbers.length === 1) {
      return [{
        id: `${kpiCode}__${marketCode}__${seasonNumbers[0]}__${weekWithYear}`,
        kpiCode,
        weekWithYear,
        value: currentValue,
        isLatestSeason: true,
      }];
    }

    // Multi-season — sort to ensure earlier season gets ending value
    const [earlier, later] = [...seasonNumbers].sort((a, b) => a.localeCompare(b));
    return [
      {
        id: `${kpiCode}__${marketCode}__${earlier}__${weekWithYear}`,
        kpiCode,
        weekWithYear,
        value: endingValue,
        isLatestSeason: false,
      },
      {
        id: `${kpiCode}__${marketCode}__${later}__${weekWithYear}`,
        kpiCode,
        weekWithYear,
        value: Math.max(0, currentValue - endingValue),
        isLatestSeason: true,
      },
    ];
  });
}
